import { useState, useCallback, useMemo } from 'react';
import { validateRequiredFields } from '../utils/fieldUtils';

/**
 * Extract base name from Adobe AcroForm field name.
 * e.g., "txtPolNo.1" → "txtPolNo", "txtPolNo" → "txtPolNo"
 */
const getBaseName = (fieldName) => {
  const dotIndex = fieldName.lastIndexOf('.');
  if (dotIndex > 0) {
    const suffix = fieldName.substring(dotIndex + 1);
    if (/^\d+$/.test(suffix)) {
      return fieldName.substring(0, dotIndex);
    }
  }
  return fieldName;
};

export const usePdfFields = (fieldConfiguration) => {
  const [fieldValues, setFieldValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // Build a map of base name → list of linked field names for auto-sync
  // e.g., "txtPolNo" → ["txtPolNo", "txtPolNo.1", "txtPolNo.2", ...]
  const linkedFieldsMap = useMemo(() => {
    const map = {};
    if (!fieldConfiguration?.fields) return map;
    
    // Group text fields by base name
    const groups = {};
    for (const field of fieldConfiguration.fields) {
      if (field.displayType === 'text') {
        const baseName = getBaseName(field.pdfFieldName);
        if (!groups[baseName]) groups[baseName] = [];
        groups[baseName].push(field.pdfFieldName);
      }
    }
    
    // Only create linked groups for fields with 2+ variants
    for (const [baseName, fieldNames] of Object.entries(groups)) {
      if (fieldNames.length > 1) {
        for (const name of fieldNames) {
          map[name] = fieldNames;
        }
        console.log(`[AutoSync] Linked fields for '${baseName}':`, fieldNames);
      }
    }
    
    return map;
  }, [fieldConfiguration]);

  const updateField = useCallback((fieldName, value) => {
    // Check if this field has linked fields (Adobe AcroForm variants)
    const linkedFields = linkedFieldsMap[fieldName];
    
    if (linkedFields && linkedFields.length > 1) {
      // Auto-sync: update all linked fields with the same value
      setFieldValues(prev => {
        const updated = { ...prev };
        for (const linkedName of linkedFields) {
          updated[linkedName] = value;
        }
        return updated;
      });
    } else {
      setFieldValues(prev => ({
        ...prev,
        [fieldName]: value
      }));
    }
    
    // Clear error for this field
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, [linkedFieldsMap]);

  const updateRadioGroup = useCallback((groupName, value) => {
    // Update all fields in the radio group
    setFieldValues(prev => ({
      ...prev,
      [groupName]: value
    }));
  }, []);

  const getFieldValue = useCallback((fieldName) => {
    return fieldValues[fieldName] || '';
  }, [fieldValues]);

  // Check if a field's visibility condition is met based on current field values
  const isConditionallyVisible = useCallback((field) => {
    if (!field.visibilityDependsOn) return true;
    
    const controllingValue = fieldValues[field.visibilityDependsOn];
    const expectedValue = field.visibilityDependsOnValue;
    
    // Checkbox condition: compare against boolean-like values
    if (expectedValue === 'true') {
      return controllingValue === true || controllingValue === 'true' || controllingValue === 'Yes';
    }
    if (expectedValue === 'false') {
      return !controllingValue || controllingValue === false || controllingValue === 'false' || controllingValue === 'No';
    }
    
    // Radio condition: exact match on the radio option pdfFieldName
    return controllingValue === expectedValue;
  }, [fieldValues]);

  /**
   * Validate required fields.
   * @param {boolean} setErrors  Write the errors into state so they render inline.
   * @param {number|null} pageNumber  When given, only validate fields on that page
   *   (used when moving to the next page); otherwise validate the whole form.
   */
  const validateFields = useCallback((setErrors = false, pageNumber = null) => {
    if (!fieldConfiguration || !fieldConfiguration.fields) {
      return { valid: true, missingFields: [], errors: {} };
    }

    const requiredFields = fieldConfiguration.fields.filter(f =>
      f.required === true &&
      f.isVisible !== false &&
      (pageNumber == null || (f.page || 1) === pageNumber)
    );
    const missingFieldNames = [];
    const errors = {};
    // Track which radio groups have already been validated to avoid duplicates
    const validatedRadioGroups = new Set();
    
    requiredFields.forEach(field => {
      // Skip validation for conditionally hidden fields
      if (!isConditionallyVisible(field)) return;

      const fieldType = field.displayType || 'text';
      
      if (fieldType === 'radio') {
        // Radio: value is stored under groupName, not pdfFieldName
        const groupName = field.groupName || field.pdfFieldName;
        if (validatedRadioGroups.has(groupName)) return; // Already validated this group
        validatedRadioGroups.add(groupName);
        
        const value = fieldValues[groupName];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          const label = field.displayLabel || groupName;
          missingFieldNames.push(label);
          // Set error on all radio fields in this group so they all show the error
          fieldConfiguration.fields
            .filter(f => (f.groupName || f.pdfFieldName) === groupName &&
                         (pageNumber == null || (f.page || 1) === pageNumber))
            .forEach(f => { errors[f.pdfFieldName] = 'Required'; });
        }
      } else if (fieldType === 'checkbox') {
        // Checkbox: value is boolean true/false
        const value = fieldValues[field.pdfFieldName];
        if (value !== true && value !== 'true' && value !== 'Yes') {
          const label = field.displayLabel || field.pdfFieldName;
          missingFieldNames.push(label);
          errors[field.pdfFieldName] = 'Required';
        }
      } else {
        // Text, number, date, signature, etc.
        const value = fieldValues[field.pdfFieldName];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          const label = field.displayLabel || field.pdfFieldName;
          missingFieldNames.push(label);
          errors[field.pdfFieldName] = 'Required';
        }
      }
    });
    
    // Only set field errors if explicitly requested (on submit or page change).
    // When validating a single page, merge so errors already shown on other pages
    // are not wiped out; a full validation replaces everything.
    if (setErrors) {
      if (pageNumber == null) {
        setFieldErrors(errors);
      } else {
        setFieldErrors(prev => {
          const merged = { ...prev };
          // Clear stale errors for this page, then apply the fresh ones.
          fieldConfiguration.fields
            .filter(f => (f.page || 1) === pageNumber)
            .forEach(f => { delete merged[f.pdfFieldName]; });
          return { ...merged, ...errors };
        });
      }
    }
    
    return {
      valid: missingFieldNames.length === 0,
      missingFields: missingFieldNames,
      errors
    };
  }, [fieldConfiguration, fieldValues, isConditionallyVisible]);

  const resetFields = useCallback(() => {
    setFieldValues({});
    setFieldErrors({});
  }, []);

  return {
    fieldValues,
    fieldErrors,
    updateField,
    updateRadioGroup,
    getFieldValue,
    validateFields,
    // Exposed so callers (e.g. Save Draft) can flag a field inline instead of
    // interrupting with a modal.
    setFieldErrors,
    resetFields
  };
};
